import { CommonModule } from '@angular/common';
import { Component, ChangeDetectorRef, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/Auth/AuthService';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { SharedModule } from '@shared/shared.module';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { MaskLast4Pipe } from "../../../pipes/cardDigitHide.pipe";
import { NzTableModule } from 'ng-zorro-antd/table';
import { HighlightPipe } from 'src/app/pipes/highlight.pipe';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { Subject, Subscription, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { FilterModalComponent, FilterConfig } from '@pages/filter-modal/filter-modal.component';
import {
  isArabicLang,
  ensureArabicFont,
  reshapeArabic,
  arabicAwareCellHook,
  drawBilingualText,
} from '../../../utils/arabic-pdf.util';

@Component({
  selector: 'app-billing-payment',
  standalone: true,
  imports: [CommonModule, SharedModule, MaskLast4Pipe, NzTableModule, NzToolTipModule, HighlightPipe, NzModalModule, NzDatePickerModule, FilterModalComponent],
  templateUrl: './billing-payment.component.html',
  styleUrl: './billing-payment.component.scss',
})
export class BillingPaymentComponent implements OnInit, OnDestroy {
  Math = Math;
  cardList: any[] = [];
  invoice: any[] = [];
  activePlans: any[] = [];
  flattenedInvoice: any[] = [];
  filteredInvoice: any[] = [];
  discountRows: any[] = [];
  private discountLookup = new Map<string, any>();
  searchText: string = '';
  activeTab: 'invoice' | 'discounts' = 'invoice';

  // Date filters
  startDateFilter: Date | null = null;
  endDateFilter: Date | null = null;

  // Pagination properties
  pageIndex: number = 1;
  pageSize: number = 10;
  totalItems: number = 0;

  // Filter
  filterModalVisible: boolean = false;
  currentFilters: any = { payment_status: 'paid' };
  @ViewChild(FilterModalComponent) filterModalComponent!: FilterModalComponent;

  filterConfig: FilterConfig = {
    showTimeline: false,
    showLevel: false,
    showStatus: false,
    showFormat: false,
    showType: false,
    showViewType: false,
    showLocation: false,
    showGender: false,
    showContest: false,
    showAge: false,
    showCompletionRate: false,
    showParticipantName: false,
    showParticipantEmail: false,
    showPaymentStatus: false,
    showPaymentMethod: true,
    showBillingMode: true,
    showPlanType: true,
    showStartDate: true,
    showEndDate: true,
    paymentStatusOptions: [
      { value: 'paid', label: 'Paid' },
      { value: 'unpaid', label: 'Unpaid' }
    ],
    paymentMethodOptions: [
      { value: 'cash', label: 'Cash' },
      { value: 'online', label: 'Online' }
    ],
    billingModeOptions: [
      { value: 'monthly', label: 'Monthly' },
      { value: 'annually', label: 'Yearly' }
    ],
    planTypeOptions: [
      { value: 'Premium Plan', label: 'Premium Plan' },
      { value: 'Growth', label: 'Growth' },
      { value: 'Enterprise', label: 'Enterprise' }
    ]
  };

  // Server-side search
  private searchSubject = new Subject<string>();
  private searchSubscription!: Subscription;
  private readonly luqtaLogoPrimaryPath = 'assets/images/luqta_favicon1.png';
  private readonly luqtaLogoFallbackPath = 'assets/images/luqta_favicon2.png';
  private luqtaLogoDataUrl: string | null = null;
  private activePlanLookup = new Map<string, any>();

  // AR (Treasure Hunt) usage
  hasArAccess = false;
  arUnlimited = false;
  arCampaignsUsed = 0;
  arCampaignsAllowed = 0;
  arUsageResetDate: string | null = null;
  arUsageResetType: 'auto' | 'manual' = 'manual';

  // Sort functions for date columns
  sortByStartDate = (a: any, b: any): number => {
    const dateA = new Date(a.start_date).getTime();
    const dateB = new Date(b.start_date).getTime();
    return dateA - dateB;
  };

  sortByEndDate = (a: any, b: any): number => {
    const dateA = new Date(a.end_date).getTime();
    const dateB = new Date(b.end_date).getTime();
    return dateA - dateB;
  };

  sortByCreatedDate = (a: any, b: any): number => {
    const dateA = new Date(a.created_date).getTime();
    const dateB = new Date(b.created_date).getTime();
    return dateA - dateB;
  };

  constructor(
    private router: Router,
    private authService: AuthService,
    private sharedService: SharedService,
    private pageService: PagesService,
    private modal: NzModalService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute
  ) { }

  ngOnInit() {
    this.applyRouteState();
    this.getCardList();
    this.loadPlanTypes();
    this.invoiceGenertion();
    this.fetchBillingDiscounts();
    this.loadArUsage();

    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(() => {
      this.pageIndex = 1;
      this.invoiceGenertion(1);
    });
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
  }

  private applyRouteState(): void {
    const queryParams = this.route.snapshot.queryParamMap;
    const tab = queryParams.get('tab');

    if (tab === 'invoice' || tab === 'discounts') {
      this.activeTab = tab;
    }

    if (queryParams.get('payment_success') === 'true') {
      setTimeout(() => {
        this.invoiceGenertion(1, false);
      }, 2000);
    }
  }

  loadArUsage() {
    this.pageService.getAll('PURCHASED_PLAN').subscribe({
      next: (response: any) => {
        const plan = Array.isArray(response)
          ? (response[0] ?? {})
          : (response?.data ?? response?.item ?? response?.result ?? response ?? {});
        this.arUnlimited = (plan?.sub_plan?.ar_unlimited ?? plan?.ar_unlimited) === true;
        // ar_unlimited implies access even if ar_access is missing/false on the response.
        this.hasArAccess = (plan?.sub_plan?.ar_access ?? plan?.ar_access) === true || this.arUnlimited;
        this.arCampaignsUsed = plan?.ar_campaigns_used ?? 0;
        this.arCampaignsAllowed = plan?.ar_campaigns_allowed ?? 0;
        this.arUsageResetDate = plan?.ar_usage_reset_date ?? null;
        this.arUsageResetType = plan?.ar_usage_reset_type === 'auto' ? 'auto' : 'manual';
      },
      error: () => { }
    });
  }

  arUsagePercent(): number {
    if (this.arUnlimited) {
      return 100;
    }
    if (!this.arCampaignsAllowed) {
      return 0;
    }
    return Math.min(100, (this.arCampaignsUsed / this.arCampaignsAllowed) * 100);
  }

  goToSubscription(): void {
    this.router.navigate(['/subscription']);
  }

  loadPlanTypes() {
    this.pageService.getAll('GET_SUBSCRIPTION_PLAN', { page: 1, per_page: 1000 }).subscribe({
      next: (response: any) => {
        const plans = Array.isArray(response) ? response
          : Array.isArray(response?.data) ? response.data
          : Array.isArray(response?.items) ? response.items
          : Array.isArray(response?.data?.items) ? response.data.items
          : [];
        if (plans && Array.isArray(plans)) {
          this.activePlans = plans;
          this.rebuildActivePlanLookup();

          // Extract unique plan types from active plans
          const uniquePlanTypes = new Set<string>();
          plans.forEach((plan: any) => {
            if (plan?.plan_name) {
              uniquePlanTypes.add(plan.plan_name);
            }
          });

          // Update filterConfig with dynamic plan types
          this.filterConfig.planTypeOptions = Array.from(uniquePlanTypes).map(planType => ({
            value: planType,
            label: planType
          }));

          if (this.invoice.length) {
            this.flattenInvoiceData();
          }
          this.cdr.detectChanges();
        }
      },
      error: (error: any) => {
        console.error('Failed to load plan types:', error);
        // Keep hardcoded options as fallback
      }
    });
  }

  getCardList() {
    this.sharedService.showLoader();
    this.pageService.getAll('BANK_CARD').subscribe({
      next: (response: any) => {

        if (response && response.length > 0) {
          let validCards = response.filter(
            (card: any) => card.card_number !== '' && card.card_number !== null
          );

          // Normalize set_primary to boolean
          validCards = validCards.map((card: any) => ({
            ...card,
            set_primary: card.set_primary === true || card.set_primary === 1 || card.set_primary === '1'
          }));

          // FIXED: Sort primary card FIRST (reversed logic)
          validCards.sort(
            (a: any, b: any) =>
              (b.set_primary ? 1 : 0) - (a.set_primary ? 1 : 0)
          );

          this.cardList = [...validCards];
        } else {
          this.cardList = [];
        }

        this.sharedService.hideLoader();
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        this.sharedService.createNotification('error', error);
        this.sharedService.hideLoader();
      },
    });
  }

  setPrimaryCard(card: any) {
    this.sharedService.showLoader();
    const data = {
      card_id: card.id,
      set_primary: true,
    };

    this.pageService.update('EDIT_BANK_CARD', data).subscribe({
      next: (response: any) => {
        // Pehle notification show karein
        this.sharedService.createNotification(
          'success',
          'Card Updated Successfully'
        );

        // Phir list refresh karein
        this.getCardList();
      },
      error: (error: any) => {
        this.sharedService.createNotification('error', error?.message);
        this.sharedService.hideLoader();
      },
    });
  }

  editCard(id: string) {
    this.router.navigate(['home/subscription/edit'], {
      queryParams: { id: id },
    });
  }

  deleteCard(card: any) {
    // Agar sirf ek hi card hai aur woh primary hai, to confirm modal show karein
    if (this.cardList.length === 1 && card.set_primary) {
      this.showConfirmModal(
        'Are you sure you want to delete the only primary card?',
        'You won’t be able to use this card again unless you add a new one.',
        () => this.proceedToDelete(card.id)
      );
      return;
    }

    // Agar card primary hai lekin aur cards bhi hain, to delete block karein
    if (card.set_primary) {
      this.showModal(
        'Primary Card Cannot Be Deleted',
        'Please set another card as primary before deleting this one.'
      );
      return;
    }

    this.showConfirmModal(
      'Delete Card',
      'Are you sure you want to delete this card?',
      () => this.proceedToDelete(card.id)
    );
  }

  proceedToDelete(cardId: string) {
    this.sharedService.showLoader();

    this.pageService
      .update('DELETE_BANK_CARD', {
        card_id: cardId,
      })
      .subscribe({
        next: (response: any) => {
          this.getCardList();

          this.sharedService.createNotification(
            'success',
            'Card deleted successfully'
          );

          this.sharedService.hideLoader();
        },
        error: (error: any) => {
          this.sharedService.createNotification('error', error);
          this.sharedService.hideLoader();
        },
      });
  }

  showModal(title: string, content: string) {
    this.modal.warning({
      nzTitle: title,
      nzContent: content,
      nzClassName: 'contest-styled-modal',
      nzCentered: true,
    });
  }

  showConfirmModal(title: string, content: string, onOkCallback: () => void) {
    this.modal.confirm({
      nzTitle: title,
      nzContent: content,
      nzOkText: 'Yes, Delete',
      nzCancelText: 'Cancel',
      nzOnOk: onOkCallback,
      nzClassName: 'contest-styled-modal',
      nzCentered: true,
    });
  }



  formatePaymentType(cardType: string) {
    switch (cardType) {
      case 'credit-card':
        return 'Credit Card';
      case 'bank-account':
        return 'Bank Account';
      case 'paypal':
        return 'Paypal';
      default:
        return 'Credit Card';
    }
  }

  showConfirm(card: any): void {
    this.modal.confirm({
      nzTitle: 'Set as Primary',
      nzContent:
        'Are you sure you want to set this card as your primary payment method?',
      nzClassName: 'contest-styled-modal',
      nzCentered: true,
      nzOnOk: () => this.setPrimaryCard(card),
      nzOnCancel: () => { },
    });
  }

  trackByCardId(index: number, card: any): number {
    return card.id;
  }

  downloadInvoice() {
    this.sharedService.showLoader();

    const queryParams: any = {
      page: 1,
      per_page: this.totalItems || 1000
    };

    if (this.searchText?.trim()) {
      queryParams.search = this.searchText.trim();
    }

    this.applyCurrentFiltersToQuery(queryParams);

    this.pageService.getAll('PAYMENT_REPORT', queryParams).subscribe({
      next: (response: any) => {
        this.sharedService.hideLoader();

        let allInvoices: any[] = [];
        if (response?.isSuccess && response.data) {
          allInvoices = Array.isArray(response.data.items) ? response.data.items : [];
        }

        if (!allInvoices || allInvoices.length === 0) {
          this.sharedService.createNotification('error', 'No Invoice Found');
          return;
        }

        void this.generatePDF(allInvoices);
      },
      error: (error: any) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification('error', error?.message || 'Failed to fetch invoices for export');
      }
    });
  }

  async generatePDF(invoiceData: any[]) {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });
    const isAr = isArabicLang();
    const arabicFontLoaded = await ensureArabicFont(doc);
    const labels = {
      invoiceId: isAr ? reshapeArabic('رقم الفاتورة') : 'Invoice ID',
      startDate: isAr ? reshapeArabic('تاريخ البدء') : 'Start Date',
      endDate: isAr ? reshapeArabic('تاريخ الانتهاء') : 'End Date',
      planType: isAr ? reshapeArabic('نوع الخطة') : 'Plan Type',
      mode: isAr ? reshapeArabic('الوضع') : 'Mode',
      paidAmount: isAr ? reshapeArabic('المبلغ المدفوع') : 'Paid Amount',
      paymentMethod: isAr ? reshapeArabic('طريقة الدفع') : 'Payment Method',
      contestAllowed: isAr ? reshapeArabic('المسابقات المسموحة') : 'Contest Allowed',
      paymentStatus: isAr ? reshapeArabic('حالة الدفع') : 'Payment Status',
      title: isAr ? 'تقرير فواتير لُقطة' : 'Luqta Invoice Report',
      name: isAr ? 'الاسم:' : 'Name:',
      email: isAr ? 'البريد الإلكتروني:' : 'Email:',
      phone: isAr ? 'الهاتف:' : 'Phone:',
      page: isAr ? 'صفحة' : 'Page',
      of: isAr ? 'من' : 'of',
    };
    const logoData = await this.getLuqtaLogoDataUrl();

    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    // Prepare all rows with user reference
    const tableRows: { user: any; row: any[] }[] = [];

    invoiceData.forEach((inv: any) => {
      const user = inv._user?.[0] || {};

      if (inv.transactions && inv.transactions.length > 0) {
        inv.transactions.forEach((txn: any) => {
          const paymentStatus = txn.payment_status || inv.payment_status;
          const paymentMethod = txn.type || inv.type || 'N/A';
          if (!this.shouldIncludeInvoiceEntry(paymentStatus, paymentMethod)) {
            return;
          }
          tableRows.push({
            user,
            row: [
              txn.id,
              txn.start_date,
              txn.end_date,
              inv._subscription_plan?.type || 'N/A',
              inv._subscription_plan?.mode || 'N/A',
              this.getPaidAmount(inv),
              paymentMethod,
              inv._subscription_plan?.contest_allowed ?? 'N/A',
              paymentStatus,
            ],
          });
        });
      } else {
        const paymentStatus = inv.payment_status;
        const paymentMethod = inv.type || 'N/A';
        if (!this.shouldIncludeInvoiceEntry(paymentStatus, paymentMethod)) {
          return;
        }
        tableRows.push({
          user,
          row: [
            inv.id,
            inv.start_date,
            inv.end_date,
            inv._subscription_plan?.type || 'N/A',
            inv._subscription_plan?.mode || 'N/A',
            this.getPaidAmount(inv),
            paymentMethod,
            inv._subscription_plan?.contest_allowed ?? 'N/A',
            paymentStatus,
          ],
        });
      }
    });

    if (tableRows.length === 0) {
      this.sharedService.createNotification('error', 'No Invoice Found');
      return;
    }

    const bodyRows = tableRows.map((entry) => entry.row);

    // Get user info from first invoice
    const user = tableRows[0]?.user || {};

    // Generate autoTable
    autoTable(doc, {
      startY: 50, // Start table lower to avoid overlap with customer info
      head: [
        [
          labels.invoiceId,
          labels.startDate,
          labels.endDate,
          labels.planType,
          labels.mode,
          labels.paidAmount,
          labels.paymentMethod,
          labels.contestAllowed,
          labels.paymentStatus,
        ],
      ],
      body: bodyRows,
      styles: {
        fontSize: 8,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [44, 62, 80],
        textColor: 255,
        fontStyle: 'bold',
        halign: isAr ? 'right' : 'center',
        valign: 'middle',
        font: isAr && arabicFontLoaded ? 'Amiri' : 'helvetica',
      },
      bodyStyles: {
        textColor: 50,
        halign: 'center',
        valign: 'middle',
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      didParseCell: arabicAwareCellHook(arabicFontLoaded),
      columnStyles: {
        0: { cellWidth: 30 },  // Invoice ID
        1: { cellWidth: 30 },  // Start Date
        2: { cellWidth: 30 },  // End Date
        3: { cellWidth: 30 },  // Plan Type
        4: { cellWidth: 25 },  // Mode
        5: { cellWidth: 30 },  // Paid Amount
        6: { cellWidth: 30 },  // Payment Method
        7: { cellWidth: 30 },  // Contest Allowed
        8: { cellWidth: 30 },  // Payment Status
      },
      margin: { top: 50, left: 10, right: 10 },

      didDrawPage: function (data) {
        if (logoData) {
          doc.addImage(logoData, 'PNG', 10, 8, 10, 10);
        }

        // Main title at top
        doc.setFontSize(22);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        drawBilingualText(doc, labels.title, pageWidth / 2, 15, arabicFontLoaded, {
          align: 'center',
        });

        // Customer Info - appears on every page at same position
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        drawBilingualText(doc, labels.name, 10, 28, arabicFontLoaded);
        doc.setFont('helvetica', 'normal');
        drawBilingualText(doc, user.name || 'N/A', 30, 28, arabicFontLoaded);

        doc.setFont('helvetica', 'bold');
        drawBilingualText(doc, labels.email, 10, 34, arabicFontLoaded);
        doc.setFont('helvetica', 'normal');
        drawBilingualText(doc, user.email || 'N/A', 30, 34, arabicFontLoaded);

        doc.setFont('helvetica', 'bold');
        drawBilingualText(doc, labels.phone, 10, 40, arabicFontLoaded);
        doc.setFont('helvetica', 'normal');
        drawBilingualText(doc, user.phone_number || 'N/A', 30, 40, arabicFontLoaded);

        // Page number at bottom
        const pageCount = doc.internal.pages.length - 1;
        doc.setFontSize(9);
        drawBilingualText(
          doc,
          isAr
            ? `${labels.page} ${data.pageNumber} ${labels.of} ${pageCount}`
            : `Page ${data.pageNumber} of ${pageCount}`,
          pageWidth - 20,
          pageHeight - 10,
          arabicFontLoaded,
          { align: 'right' }
        );
      },
    });

    const timestamp = new Date().toISOString().split('T')[0];
    doc.save(`Invoice_Report_${timestamp}.pdf`);
    this.sharedService.createNotification('success', 'PDF downloaded successfully');
  }

  downloadExcel() {
    this.sharedService.showLoader();

    const queryParams: any = {
      page: 1,
      per_page: this.totalItems || 1000
    };

    if (this.searchText?.trim()) {
      queryParams.search = this.searchText.trim();
    }

    this.applyCurrentFiltersToQuery(queryParams);

    this.pageService.getAll('PAYMENT_REPORT', queryParams).subscribe({
      next: (response: any) => {
        this.sharedService.hideLoader();

        let allInvoices: any[] = [];
        if (response?.isSuccess && response.data) {
          allInvoices = Array.isArray(response.data.items) ? response.data.items : [];
        }

        if (!allInvoices || allInvoices.length === 0) {
          this.sharedService.createNotification('error', 'No Invoice Found');
          return;
        }

        this.generateExcel(allInvoices);
      },
      error: (error: any) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification('error', error?.message || 'Failed to fetch invoices for export');
      }
    });
  }

  generateExcel(invoiceData: any[]) {
    const excelData: any[] = [];

    invoiceData.forEach((inv: any) => {
      if (inv.transactions && inv.transactions.length > 0) {
        inv.transactions.forEach((txn: any) => {
          const paymentStatus = txn.payment_status || inv.payment_status;
          const paymentMethod = txn.type || inv.type || 'N/A';
          if (!this.shouldIncludeInvoiceEntry(paymentStatus, paymentMethod)) {
            return;
          }
          excelData.push({
            'Invoice ID': txn.id,
            'Start Date': txn.start_date,
            'End Date': txn.end_date,
            'Plan Type': inv._subscription_plan?.type || 'N/A',
            'Mode': inv._subscription_plan?.mode || 'N/A',
            'Paid Amount': this.getPaidAmount(inv),
            'Payment Method': paymentMethod,
            'Contest Allowed': inv._subscription_plan?.contest_allowed ?? 'N/A',
            'Payment Status': paymentStatus
          });
        });
      } else {
        const paymentStatus = inv.payment_status;
        const paymentMethod = inv.type || 'N/A';
        if (!this.shouldIncludeInvoiceEntry(paymentStatus, paymentMethod)) {
          return;
        }
        excelData.push({
          'Invoice ID': inv.id,
          'Start Date': inv.start_date,
          'End Date': inv.end_date,
          'Plan Type': inv._subscription_plan?.type || 'N/A',
          'Mode': inv._subscription_plan?.mode || 'N/A',
          'Paid Amount': this.getPaidAmount(inv),
          'Payment Method': paymentMethod,
          'Contest Allowed': inv._subscription_plan?.contest_allowed ?? 'N/A',
          'Payment Status': paymentStatus
        });
      }
    });

    if (excelData.length === 0) {
      this.sharedService.createNotification('error', 'No Invoice Found');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    worksheet['!cols'] = [
      { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
      { wch: 12 }, { wch: 15 }, { wch: 18 }, { wch: 18 }, { wch: 15 }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoices');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    const timestamp = new Date().toISOString().split('T')[0];
    saveAs(blob, `Invoice_Report_${timestamp}.xlsx`);
    this.sharedService.createNotification('success', 'Excel downloaded successfully');
  }

  invoiceGenertion(page: number = 1, showLoader: boolean = true) {
    if (showLoader) this.sharedService.showLoader();

    const queryParams: any = {
      page: page,
      per_page: this.pageSize
    };

    if (this.searchText?.trim()) {
      queryParams.search = this.searchText.trim();
    }

    if (this.startDateFilter) {
      queryParams.start_date = this.startDateFilter.toISOString().split('T')[0];
    }

    if (this.endDateFilter) {
      queryParams.end_date = this.endDateFilter.toISOString().split('T')[0];
    }

    // Apply filters from filter modal
    this.applyCurrentFiltersToQuery(queryParams);

    this.pageService.getAll('PAYMENT_REPORT', queryParams).subscribe({
      next: (response: any) => {
        if (response?.isSuccess && response.data) {
          // Handle server-side pagination response
          this.totalItems = response.data.itemsTotal || 0;
          this.pageIndex = response.data.curPage || page;

          if (Array.isArray(response.data.items) && response.data.items.length > 0) {
            this.invoice = response.data.items;
            this.flattenInvoiceData();
          } else {
            this.invoice = [];
            this.flattenedInvoice = [];
            this.filteredInvoice = [];
          }
        }
        this.sharedService.hideLoader();
      },
      error: (error: any) => {
        this.sharedService.createNotification(
          'error',
          error?.message || 'An error occurred while fetching invoices.'
        );
        this.sharedService.hideLoader();
      },
    });
  }

  flattenInvoiceData() {
    this.flattenedInvoice = [];
    this.invoice.forEach((inv: any) => {
      const user = inv._user?.[0] || {};
      const plan = inv._subscription_plan || {};
      const activePlan = this.getActivePlanForSubscription(plan, inv);
      const discountInfo = this.buildDiscountInfo(inv, plan);
      const fallback = this.buildFallbackPricing(inv, plan, discountInfo);
      const overrides = this.detectPlanOverrides(inv, plan, activePlan);
      console.log('Invoice raw data:', JSON.stringify(inv, null, 2));
      console.log('Plan data:', JSON.stringify(plan, null, 2));
      console.log('Active plan match:', JSON.stringify(activePlan, null, 2));
      console.log('Detected overrides:', overrides);

      if (inv.transactions && inv.transactions.length > 0) {
        inv.transactions.forEach((txn: any) => {
          const paymentStatus = txn.payment_status || inv.payment_status;
          const paymentMethod = txn.type || inv.type || 'N/A';
          const currentPlanName = this.getPlanDisplayName(plan);
          if (!this.shouldIncludeInvoiceEntry(paymentStatus, paymentMethod)) {
            return;
          }
          this.flattenedInvoice.push({
            id: txn.id,
            invoice_id: inv.id,
            billing_payment_id: inv.id,
            start_date: txn.start_date,
            end_date: txn.end_date,
            created_date: inv.created_at ? new Date(inv.created_at).toISOString().split('T')[0] : 'N/A',
            payment_status: paymentStatus,
            plan_type: currentPlanName,
            mode: plan.mode || 'N/A',
            paid_amount: this.getPaidAmount(inv),
            contest_allowed: this.getContestAllowedValue(inv, plan),
            participants: this.getParticipantsValue(inv, plan),
            payment_method: paymentMethod,
            original_amount: fallback.original_amount,
            _raw_discount_saved: fallback._raw_discount_saved,
            ...discountInfo,
            _user: user,
            _plan: plan,
            _active_plan: activePlan,
            _overrides: overrides
          });
        });
      } else {
        const paymentStatus = inv.payment_status;
        const paymentMethod = inv.type || 'N/A';
        const currentPlanName = this.getPlanDisplayName(plan);
        if (!this.shouldIncludeInvoiceEntry(paymentStatus, paymentMethod)) {
          return;
        }
        this.flattenedInvoice.push({
          id: inv.id,
          invoice_id: inv.id,
          billing_payment_id: inv.id,
          start_date: inv.start_date,
          end_date: inv.end_date,
          created_date: inv.created_at ? new Date(inv.created_at).toISOString().split('T')[0] : 'N/A',
          payment_status: paymentStatus,
          plan_type: currentPlanName,
          mode: plan.mode || 'N/A',
          paid_amount: discountInfo.discounted_amount || this.getPaidAmount(inv),
          contest_allowed: this.getContestAllowedValue(inv, plan),
          participants: this.getParticipantsValue(inv, plan),
          payment_method: paymentMethod,
          original_amount: fallback.original_amount,
          _raw_discount_saved: fallback._raw_discount_saved,
          ...discountInfo,
          _user: user,
          _plan: plan,
          _active_plan: activePlan,
          _overrides: overrides
        });
      }
    });

    this.applyDiscountsToInvoices();
    this.updateBillingFilterOptions();
  }

  private detectPlanOverrides(inv: any, plan: any, activePlan?: any): Record<string, boolean> {
    const overrides: Record<string, boolean> = {};
    const flagOverride = (key: string) => {
      overrides[this.normalizeOverrideKey(key)] = true;
    };
    const getValue = (source: any, keys: string[]) => {
      for (const key of keys) {
        const value = source?.[key];
        if (value !== undefined && value !== null && value !== '') {
          return value;
        }
      }
      return null;
    };

    // Check for explicit overrides from API
    if (inv.overrides && typeof inv.overrides === 'object') {
      Object.keys(inv.overrides).forEach(key => flagOverride(key));
    }
    if (Array.isArray(inv.overridden_fields)) {
      inv.overridden_fields.forEach((key: string) => flagOverride(key));
    }

    const sourcePlan = activePlan || plan;
    const differs = (current: any, reference: any): boolean => {
      if (current == null || reference == null) {
        return false;
      }
      return this.normalizeComparableValue(current) !== this.normalizeComparableValue(reference);
    };
    const getCurrent = (...keys: string[]) => getValue(inv, keys) ?? getValue(plan, keys);
    const getReference = (...keys: string[]) => getValue(sourcePlan, keys);
    const markIfChanged = (key: string, currentKeys: string[], referenceKeys: string[] = currentKeys) => {
      const current = getCurrent(...currentKeys);
      const reference = getReference(...referenceKeys);
      if (differs(current, reference)) {
        overrides[key] = true;
      }
    };

    // Detect value differences against the active plan catalog
    markIfChanged('mode', ['mode', 'plan_mode', 'Mode'], ['mode', 'plan_mode', 'Mode']);
    markIfChanged('contest_allowed', ['contest_allowed', 'Contest_allowed'], ['contest_allowed', 'Contest_allowed']);
    markIfChanged('participants', ['participants', 'Participants'], ['participants', 'Participants']);
    markIfChanged('integrated_contests', ['integrated_contests', 'Integrated_Contests'], ['integrated_contests', 'Integrated_Contests']);
    markIfChanged('ads_view_access', ['ads_view_access', 'Ads_View_Access'], ['ads_view_access', 'Ads_View_Access']);
    markIfChanged('support_access', ['support_access', 'Support_Access'], ['support_access', 'Support_Access']);
    markIfChanged('vip_view', ['vip_view', 'VIP_View'], ['vip_view', 'VIP_View']);
    markIfChanged('report_view', ['report_view'], ['report_view']);
    markIfChanged('ai_attributes', ['ai_attributes'], ['ai_attributes']);
    markIfChanged('sdk_features', ['sdk_features'], ['sdk_features']);
    markIfChanged('sdk_price', ['sdk_price'], ['sdk_price']);
    markIfChanged('sdk_billing_cycle', ['sdk_billing_cycle'], ['sdk_billing_cycle']);
    markIfChanged('monthly_price', ['monthly_price'], ['monthly_price']);
    markIfChanged('yearly_price', ['yearly_price'], ['yearly_price']);
    markIfChanged('currency', ['currency'], ['currency']);
    if (getValue(inv, ['adjusted_amount', 'adjustedAmount']) != null) {
      overrides['paid_amount'] = true;
    } else if (activePlan) {
      const currentPaid = this.getCurrentPaidAmountForComparison(inv, plan);
      const referencePaid = this.getActivePlanPriceForMode(sourcePlan, getCurrent('mode', 'plan_mode', 'Mode'));
      if (currentPaid != null && referencePaid != null && differs(currentPaid, referencePaid)) {
        overrides['paid_amount'] = true;
      }
    }

    return overrides;
  }

  private normalizeOverrideKey(key: string): string {
    return String(key || '')
      .trim()
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .replace(/[\s-]+/g, '_')
      .toLowerCase();
  }

  private normalizeComparableValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value).trim().toLowerCase().replace(/\s+/g, ' ');
  }

  private rebuildActivePlanLookup(): void {
    this.activePlanLookup.clear();

    this.activePlans.forEach((plan: any) => {
      const keys = [
        plan?.id,
        plan?.plan_name,
        plan?.type
      ];

      keys.forEach((key: any) => {
        const normalized = String(key ?? '').trim().toLowerCase();
        if (normalized) {
          this.activePlanLookup.set(normalized, plan);
        }
      });
    });
  }

  private getActivePlanForSubscription(plan: any, inv: any): any {
    const candidates = [
      plan?.id,
      plan?.plan_name,
      plan?.type,
      inv?.plan_name,
      inv?.plan_type,
      inv?.type
    ];

    for (const candidate of candidates) {
      const normalized = String(candidate ?? '').trim().toLowerCase();
      if (normalized && this.activePlanLookup.has(normalized)) {
        return this.activePlanLookup.get(normalized);
      }
    }

    return null;
  }

  private getPlanDisplayName(plan: any): string {
    return plan?.plan_name || plan?.type || 'N/A';
  }

  private getContestAllowedValue(inv: any, plan: any): any {
    return inv?.contest_allowed ?? inv?.Contest_allowed ?? plan?.contest_allowed ?? plan?.Contest_allowed ?? 'N/A';
  }

  private getParticipantsValue(inv: any, plan: any): any {
    return inv?.participants ?? inv?.Participants ?? plan?.participants ?? plan?.Participants ?? 'N/A';
  }

  private getCurrentPaidAmountForComparison(inv: any, plan: any): number | null {
    const currentPaid = this.parseAmount(inv?.paid_amount) ?? this.parseAmount(this.getPaidAmount(inv));
    if (currentPaid != null) {
      return currentPaid;
    }
    const monthly = this.parseAmount(plan?.monthly_price);
    const yearly = this.parseAmount(plan?.yearly_price);
    return monthly ?? yearly ?? this.parseAmount(plan?.price);
  }

  private getActivePlanPriceForMode(plan: any, mode: any): number | null {
    const modeValue = String(mode || '').toLowerCase();
    if (['yearly', 'annually', 'annual'].includes(modeValue)) {
      return this.parseAmount(plan?.yearly_price) ?? this.parseAmount(plan?.price);
    }
    return this.parseAmount(plan?.monthly_price) ?? this.parseAmount(plan?.price);
  }

  getOverrideTooltip(row: any, fieldKey: string, label: string): string | null {
    const current = this.getOverrideFieldValue(row, fieldKey, true);
    const previous = this.getOverrideFieldValue(row, fieldKey, false);

    if (current == null || previous == null) {
      return null;
    }

    const currentText = this.formatOverrideValue(current, fieldKey);
    const previousText = this.formatOverrideValue(previous, fieldKey);

    if (!currentText || !previousText || currentText === previousText) {
      return null;
    }

    return `${label} overridden from ${previousText} to ${currentText}`;
  }

  private getOverrideFieldValue(row: any, fieldKey: string, current: boolean): any {
    const plan = current ? (row?._plan || {}) : (row?._active_plan || row?._plan || {});
    switch (fieldKey) {
      case 'mode':
        return current
          ? (row?.mode ?? row?._plan?.mode ?? row?._plan?.plan_mode ?? row?._plan?.Mode)
          : (plan?.mode ?? plan?.plan_mode ?? plan?.Mode);
      case 'contest_allowed':
        return current
          ? (row?.contest_allowed ?? row?.Contest_allowed ?? row?._plan?.contest_allowed ?? row?._plan?.Contest_allowed)
          : (plan?.contest_allowed ?? plan?.Contest_allowed);
      case 'participants':
        return current
          ? (row?.participants ?? row?.Participants ?? row?._plan?.participants ?? row?._plan?.Participants)
          : (plan?.participants ?? plan?.Participants);
      case 'paid_amount':
        return current
          ? (row?.paid_amount ?? row?._plan?.paid_amount)
          : (plan?.paid_amount ?? plan?.original_amount ?? this.getActivePlanPriceForMode(plan, row?.mode));
      default:
        return current ? row?.[fieldKey] : plan?.[fieldKey];
    }
  }

  private formatOverrideValue(value: any, fieldKey: string): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (['paid_amount', 'monthly_price', 'yearly_price', 'price'].includes(fieldKey)) {
      const parsed = this.parseAmount(value);
      if (parsed !== null) {
        return this.formatCurrency(parsed);
      }
    }

    return String(value);
  }

  private normalizePaymentStatus(status: any): string {
    return String(status || '').trim().toLowerCase();
  }

  private normalizePaymentMethod(method: any): string {
    return String(method || '').trim().toLowerCase();
  }

  private shouldIncludeInvoiceEntry(status: any, method: any): boolean {
    const normalizedStatus = this.normalizePaymentStatus(status);
    const normalizedMethod = this.normalizePaymentMethod(method);
    const isOnline = ['online', 'stripe', 'card', 'credit-card'].includes(normalizedMethod);

    if (isOnline && normalizedStatus !== 'paid') {
      return false;
    }

    return true;
  }

  private applyCurrentFiltersToQuery(queryParams: any): void {
    if (this.currentFilters.payment_status) {
      queryParams.payment_status = this.currentFilters.payment_status;
    }
    if (this.currentFilters.payment_method) {
      // Backend uses `type` for payment method in billing report filters.
      queryParams.type = this.currentFilters.payment_method;
    }
    if (this.currentFilters.billing_mode) {
      queryParams.billing_mode = this.currentFilters.billing_mode;
    }
    if (this.currentFilters.plan_type) {
      queryParams.plan_type = this.currentFilters.plan_type;
    }
    if (this.currentFilters.start_date) {
      const sd = new Date(this.currentFilters.start_date);
      queryParams.start_date = sd.toISOString().split('T')[0];
    }
    if (this.currentFilters.end_date) {
      const ed = new Date(this.currentFilters.end_date);
      queryParams.end_date = ed.toISOString().split('T')[0];
    }
  }

  private updateBillingFilterOptions(): void {
    const normalizedMode = (value: string): string => {
      const lower = value.toLowerCase();
      return lower === 'yearly' ? 'annually' : lower;
    };

    const modeSet = new Set<string>();
    const planSet = new Set<string>();

    this.flattenedInvoice.forEach((item: any) => {
      const mode = String(item.mode || '').trim();
      const planType = String(item.plan_type || '').trim();

      if (mode) {
        modeSet.add(normalizedMode(mode));
      }
      if (planType) {
        planSet.add(planType);
      }
    });

    // Billing Mode and Plan Type options are hardcoded in filterConfig
  }

  onSearch() {
    this.searchSubject.next(this.searchText);
  }

  setActiveTab(tab: 'invoice' | 'discounts'): void {
    this.activeTab = tab;
  }

  get filteredDiscounts(): any[] {
    if (!this.searchText?.trim()) {
      return this.discountRows;
    }
    const term = this.searchText.toLowerCase().trim();
    return this.discountRows.filter(row =>
      String(row.invoice_id || '').toLowerCase().includes(term) ||
      String(row.plan_type || '').toLowerCase().includes(term) ||
      String(row.mode || '').toLowerCase().includes(term) ||
      String(row.payment_status || '').toLowerCase().includes(term) ||
      String(row.discount_saved || '').toLowerCase().includes(term)
    );
  }

  getPaidAmount(inv: any): string {
    if (inv.paid_amount) return '$' + inv.paid_amount;
    const priceId = inv._subscription_plan?.price_id;
    if (priceId) {
      const lastUnderscore = priceId.lastIndexOf('_');
      if (lastUnderscore !== -1) {
        const amount = priceId.substring(lastUnderscore + 1);
        if (amount && !isNaN(Number(amount))) return '$' + amount;
      }
    }
    return 'N/A';
  }

  fetchBillingDiscounts(): void {
    const discountParams = { billing_payment_id: 0 };

    this.pageService.getAll('BILLING_DISCOUNT', discountParams).subscribe({
      next: (response: any) => {
        const allItems = Array.isArray(response?.history) ? response.history
          : Array.isArray(response) ? response
          : Array.isArray(response?.data) ? response.data
          : Array.isArray(response?.items) ? response.items
          : [];

        this.discountRows = allItems.map((item: any) => {
          const isYearly = String(item.billing_cycle).toLowerCase().includes('annual') || String(item.billing_cycle).toLowerCase().includes('year');
          const originalAmount = isYearly ? item.original_yearly_price : item.original_monthly_price;
          const discountedAmount = item.discounted_amount;
          const discountSaved = item.discount_saved;

          return {
            id: item.id,
            invoice_id: item.id,
            billing_payment_id: item.subscription_id ?? item.id,
            subscription_id: item.subscription_id,
            plan_type: item.plan_name || 'N/A',
            mode: item.billing_cycle || 'N/A',
            original_amount: originalAmount != null ? this.formatCurrency(originalAmount) : 'N/A',
            discounted_amount: discountedAmount != null ? this.formatCurrency(discountedAmount) : 'N/A',
            discount_saved: discountSaved != null ? this.formatCurrency(discountSaved) : 'N/A',
            _raw_discount_saved: discountSaved ?? 0,
            start_date: item.start_date,
            end_date: item.end_date,
            payment_status: item.payment_status || 'N/A',
            paid_amount: discountedAmount != null ? this.formatCurrency(discountedAmount) : 'N/A',
            payment_method: item.type || 'N/A',
            contest_allowed: item.contest_allowed ?? 'N/A',
            currency: item.currency || 'USD'
          };
        });

        this.refreshDiscountLookup();
        this.applyDiscountsToInvoices();
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        console.error('Failed to fetch billing discounts:', error);
      }
    });
  }

  private refreshDiscountLookup(): void {
    this.discountLookup.clear();

    this.discountRows.forEach((row: any) => {
      const keys = [
        row?.subscription_id,
        row?.billing_payment_id,
        row?.invoice_id,
        row?.id,
        row?.transaction_id
      ];

      keys.forEach((key: any) => {
        const normalized = String(key || '').trim();
        if (normalized) {
          this.discountLookup.set(normalized, row);
        }
      });
    });
  }

  private applyDiscountsToInvoices(): void {
    if (!this.flattenedInvoice.length) {
      this.filteredInvoice = [];
      return;
    }

    if (!this.discountLookup.size) {
      this.filteredInvoice = [...this.flattenedInvoice];
      return;
    }

    this.flattenedInvoice = this.flattenedInvoice.map((row: any) => {
      const billingKey = String(row?.billing_payment_id || '').trim();
      const invoiceKey = String(row?.invoice_id || '').trim();
      const primaryKey = String(row?.id || '').trim();
      const discount = (billingKey ? this.discountLookup.get(billingKey) : undefined)
        || (invoiceKey ? this.discountLookup.get(invoiceKey) : undefined)
        || (primaryKey ? this.discountLookup.get(primaryKey) : undefined);
      if (!discount) return row;

      return {
        ...row,
        original_amount: discount.original_amount ?? row.original_amount,
        discounted_amount: discount.discounted_amount ?? row.discounted_amount,
        discount_display: discount.discount_display ?? row.discount_display,
        discount_saved: discount.discount_saved ?? row.discount_saved,
        _raw_discount_saved: discount._raw_discount_saved ?? row._raw_discount_saved,
        paid_amount: row.paid_amount
      };
    });

    this.filteredInvoice = [...this.flattenedInvoice];
  }

  private parseAmount(value: any): number | null {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const cleaned = String(value).replace(/[^0-9.]/g, '');
    if (!cleaned) return null;
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : null;
  }

  private formatCurrency(value: number): string {
    if (!Number.isFinite(value)) return 'N/A';
    const fixed = value % 1 === 0 ? value.toFixed(0) : value.toFixed(2);
    return `$${fixed}`;
  }

  private buildDiscountInfoFromDiscount(discount: any, mode: string | undefined): { original_amount?: string; discounted_amount?: string; discount_display?: string } {
    if (!discount) return {};

    const modeRaw = String(mode || '').toLowerCase();
    const isYearly = ['yearly', 'annually', 'annual'].includes(modeRaw);

    const originalRaw = isYearly ? discount.original_yearly_amount : discount.original_monthly_amount;
    const discountedRaw = isYearly ? discount.discounted_yearly_amount : discount.discounted_monthly_amount;
    const valueRaw = isYearly ? discount.yearly_discount_value : discount.monthly_discount_value;

    const originalAmount = this.parseAmount(originalRaw);
    let discountedAmount = this.parseAmount(discountedRaw);

    if (discountedAmount === null && originalAmount !== null) {
      const discountValue = Number(valueRaw);
      if (discount.discount_type && Number.isFinite(discountValue)) {
        if (discount.discount_type === 'percentage') {
          discountedAmount = originalAmount - (originalAmount * discountValue) / 100;
        } else if (discount.discount_type === 'fixed') {
          discountedAmount = originalAmount - discountValue;
        }
      }
      if (discountedAmount !== null && discountedAmount < 0) {
        discountedAmount = 0;
      }
    }

    const originalDisplay = originalAmount !== null ? this.formatCurrency(originalAmount) : 'N/A';
    const discountedDisplay = discountedAmount !== null ? this.formatCurrency(discountedAmount) : 'N/A';

    let discountDisplay = '—';
    const discountValueNum = Number(valueRaw);
    if (discount.discount_type === 'percentage' && Number.isFinite(discountValueNum)) {
      discountDisplay = `${discountValueNum}%`;
    } else if (discount.discount_type === 'fixed' && Number.isFinite(discountValueNum)) {
      discountDisplay = this.formatCurrency(discountValueNum);
    } else if (originalAmount !== null && discountedAmount !== null && originalAmount > discountedAmount) {
      discountDisplay = this.formatCurrency(originalAmount - discountedAmount);
    }

    return {
      original_amount: originalDisplay,
      discounted_amount: discountedDisplay,
      discount_display: discountDisplay
    };
  }

  private buildFallbackPricing(inv: any, plan: any, discountInfo: any): { original_amount: string; _raw_discount_saved: number } {
    if (discountInfo?.original_amount && discountInfo.original_amount !== 'N/A') {
      return {
        original_amount: discountInfo.original_amount,
        _raw_discount_saved: 0
      };
    }

    const modeRaw = String(plan?.mode || inv?.mode || '').toLowerCase();
    const isYearly = ['yearly', 'annually', 'annual'].includes(modeRaw);
    const priceRaw = isYearly ? plan?.yearly_price : plan?.monthly_price;
    let originalAmount = this.parseAmount(priceRaw);

    // Fallback: parse from price_id (e.g. "premium_monthly_49")
    if (originalAmount === null && plan?.price_id) {
      const lastUnderscore = String(plan.price_id).lastIndexOf('_');
      if (lastUnderscore !== -1) {
        const amountStr = String(plan.price_id).substring(lastUnderscore + 1);
        const parsed = Number(amountStr);
        if (Number.isFinite(parsed)) originalAmount = parsed;
      }
    }

    // Final fallback: use paid amount as original
    if (originalAmount === null) {
      originalAmount = this.parseAmount(inv?.paid_amount) ?? this.parseAmount(this.getPaidAmount(inv));
    }

    if (originalAmount !== null) {
      const paidRaw = this.parseAmount(inv?.paid_amount) ?? this.parseAmount(this.getPaidAmount(inv));
      const saved = paidRaw !== null ? (paidRaw - originalAmount) : 0;
      return {
        original_amount: this.formatCurrency(originalAmount),
        _raw_discount_saved: saved
      };
    }

    return {
      original_amount: 'N/A',
      _raw_discount_saved: 0
    };
  }

  private buildDiscountInfo(inv: any, plan: any): { original_amount?: string; discounted_amount?: string; discount_display?: string; _raw_discount_saved?: number } {
    const discount = inv?.discount ?? inv?._discount ?? plan?.discount ?? plan?._discount ?? inv?._subscription_plan?.discount;
    if (!discount) return {};

    const modeRaw = String(plan?.mode || inv?.mode || '').toLowerCase();
    const isYearly = ['yearly', 'annually', 'annual'].includes(modeRaw);

    const originalRaw = isYearly ? discount.original_yearly_amount : discount.original_monthly_amount;
    const discountedRaw = isYearly ? discount.discounted_yearly_amount : discount.discounted_monthly_amount;
    const valueRaw = isYearly ? discount.yearly_discount_value : discount.monthly_discount_value;

    const originalAmount = this.parseAmount(originalRaw);
    let discountedAmount = this.parseAmount(discountedRaw);

    if (discountedAmount === null && originalAmount !== null) {
      const discountValue = Number(valueRaw);
      if (discount.discount_type && Number.isFinite(discountValue)) {
        if (discount.discount_type === 'percentage') {
          discountedAmount = originalAmount - (originalAmount * discountValue) / 100;
        } else if (discount.discount_type === 'fixed') {
          discountedAmount = originalAmount - discountValue;
        }
      }
      if (discountedAmount !== null && discountedAmount < 0) {
        discountedAmount = 0;
      }
    }

    const originalDisplay = originalAmount !== null ? this.formatCurrency(originalAmount) : 'N/A';
    const discountedDisplay = discountedAmount !== null ? this.formatCurrency(discountedAmount) : 'N/A';

    let discountDisplay = '—';
    const discountValueNum = Number(valueRaw);
    if (discount.discount_type === 'percentage' && Number.isFinite(discountValueNum)) {
      discountDisplay = `${discountValueNum}%`;
    } else if (discount.discount_type === 'fixed' && Number.isFinite(discountValueNum)) {
      discountDisplay = this.formatCurrency(discountValueNum);
    } else if (originalAmount !== null && discountedAmount !== null && originalAmount > discountedAmount) {
      discountDisplay = this.formatCurrency(originalAmount - discountedAmount);
    }

    let rawSaved = 0;
    if (originalAmount !== null && discountedAmount !== null) {
      rawSaved = discountedAmount - originalAmount;
    }

    return {
      original_amount: originalDisplay,
      discounted_amount: discountedDisplay,
      discount_display: discountDisplay,
      _raw_discount_saved: rawSaved
    };
  }

  onDateFilterChange() {
    this.pageIndex = 1;
    this.invoiceGenertion(1);
  }

  getStatusClass(status: string): string {
    if (!status) return '';

    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'paid':
        return 'status-paid';
      case 'unpaid':
        return 'status-unpaid';
      default:
        return '';
    }
  }

  onPageIndexChange(page: number): void {
    this.pageIndex = page;
    this.invoiceGenertion(page);
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.pageIndex = 1;
    this.invoiceGenertion(1);
  }

  openFilterModal(): void {
    this.filterModalVisible = true;
  }

  onFiltersApplied(filters: any): void {
    this.currentFilters = { ...filters, payment_status: 'paid' };
    this.pageIndex = 1;
    this.invoiceGenertion(1);
  }

  onFiltersReset(): void {
    this.currentFilters = { payment_status: 'paid' };
    this.pageIndex = 1;
    this.invoiceGenertion(1);
  }

  hasActiveFilters(): boolean {
    return Object.keys(this.currentFilters).some(key => {
      if (key === 'payment_status' && this.currentFilters[key] === 'paid') {
        return false;
      }
      const value = this.currentFilters[key];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== null && value !== '' && value !== undefined;
    });
  }

  clearAllFilters(): void {
    this.currentFilters = { payment_status: 'paid' };
    if (this.filterModalComponent) {
      this.filterModalComponent.filterForm.reset({
        payment_status: 'paid',
        payment_method: null,
        billing_mode: null,
        plan_type: null,
        start_date: null,
        end_date: null
      });
    }
    this.pageIndex = 1;
    this.invoiceGenertion(1);
  }

  async downloadSingleInvoice(data: any) {
    this.sharedService.showLoader();
    const mergedData = await this.enrichInvoiceWithDiscount(data);

    // Fetch invoice details from API
    let invoiceDetails: any = {};
    try {
      const subscriptionId = data?.billing_payment_id ?? data?.invoice_id ?? data?.id ?? 0;
      const detailsResponse: any = await firstValueFrom(
        this.pageService.getAll('INVOICE_DETAILS', { subscription_id: subscriptionId })
      );
      invoiceDetails = detailsResponse || {};
    } catch (e) {
      console.error('Failed to fetch invoice details:', e);
    }

    // Fetch current plan details
    let purchasedPlan: any = {};
    try {
      const planResponse: any = await firstValueFrom(
        this.pageService.getAll('PURCHASED_PLAN')
      );
      purchasedPlan = planResponse || {};
    } catch (e) {
      console.error('Failed to fetch purchased plan:', e);
    }

    // Load Cairo font for Arabic support
    await this.loadCairoFont();
    await document.fonts.ready;

    const isAr = isArabicLang();
    // Canvas text is rendered by the browser's own text engine (Cairo font),
    // which already shapes/joins Arabic glyphs correctly — unlike jsPDF's
    // built-in fonts, so labels are simply swapped, not reshaped.
    const t = (en: string, ar: string): string => (isAr ? ar : en);

    const user = mergedData._user || {};
    const details = invoiceDetails;
    const subPlan = purchasedPlan.sub_plan || {};
    const plan = { ...subPlan, ...(details._subscription_plan || {}), ...(mergedData._plan || {}) };
    const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });

    const leftMargin = 50;
    const rightEdge = 750;
    const fontFamily = "'Cairo', sans-serif";
    const pageWidth = 800;
    const pageHeight = 1600;

    const scale = 2;
    const canvas = document.createElement('canvas');
    canvas.width = pageWidth * scale;
    canvas.height = pageHeight * scale;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(scale, scale);

    // White background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, pageWidth, pageHeight);

    // Helper to draw a label-value row
    const drawField = (label: string, value: string, labelXPos: number, valueXPos: number, yPos: number) => {
      ctx.font = `bold 12px ${fontFamily}`;
      ctx.fillStyle = '#2C3E50';
      ctx.textAlign = 'left';
      ctx.fillText(label, labelXPos, yPos);
      ctx.font = `12px ${fontFamily}`;
      ctx.fillStyle = '#3C3C3C';
      ctx.fillText(value, valueXPos, yPos);
    };

    // === HEADER ===
    const logoImg = await this.loadImageForCanvas('assets/images/luqta_favicon1.png');
    if (logoImg) {
      ctx.drawImage(logoImg, leftMargin, 35, 30, 30);
    }
    ctx.font = `bold 28px ${fontFamily}`;
    ctx.fillStyle = '#2C3E50';
    ctx.textAlign = 'left';
    ctx.fillText('LUQTA', leftMargin + 38, 60);

    ctx.font = `bold 26px ${fontFamily}`;
    ctx.textAlign = 'right';
    ctx.fillText(t('INVOICE', 'فاتورة'), rightEdge, 55);

    ctx.font = `12px ${fontFamily}`;
    ctx.fillStyle = '#505050';
    ctx.fillText(`${t('Invoice #:', 'رقم الفاتورة:')} ${mergedData.id || 'N/A'}`, rightEdge, 75);
    ctx.fillText(`${t('Date:', 'التاريخ:')} ${mergedData.created_date || today}`, rightEdge, 92);

    // Divider
    ctx.strokeStyle = '#C8C8C8';
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(leftMargin, 110);
    ctx.lineTo(rightEdge, 110);
    ctx.stroke();

    // Helper to draw a section divider
    const drawSectionDivider = (yPos: number) => {
      ctx.strokeStyle = '#E0E0E0';
      ctx.lineWidth = 0.6;
      ctx.beginPath();
      ctx.moveTo(leftMargin, yPos);
      ctx.lineTo(rightEdge, yPos);
      ctx.stroke();
    };

    // === BILL TO (left) & COMPANY INFO (right) ===
    let y = 130;
    const labelX = leftMargin;
    const valueX = leftMargin + 140;
    const companyLabelX = 450;
    const companyValueX = 590;

    // Left: Bill To
    ctx.font = `bold 15px ${fontFamily}`;
    ctx.fillStyle = '#2C3E50';
    ctx.textAlign = 'left';
    ctx.fillText(t('Bill To:', 'الفاتورة إلى:'), labelX, y);

    // Right: Company Info
    ctx.fillText(t('From:', 'من:'), companyLabelX, y);

    y += 26;
    const companyName = details.company_name || 'LUQTA';
    const companyAddress = details.company_address || details.address || '';
    const companyTrn = details.trn_number || details.vat_number || details.tax_number || '';

    // Bill To fields
    const billToFields = [
      { label: t('Client Name:', 'اسم العميل:'), value: user.name || 'N/A' },
      { label: t('Company:', 'الشركة:'), value: user.company_name || user.name || 'N/A' },
      { label: t('Email:', 'البريد الإلكتروني:'), value: user.email || 'N/A' },
      { label: t('Phone:', 'الهاتف:'), value: user.phone_number || 'N/A' },
      { label: t('Country:', 'الدولة:'), value: user.country || 'N/A' },
    ];
    // Add customer tax number if available
    const customerTax = user.trn_number || user.vat_number || user.tax_number || details.customer_tax_number || '';
    if (customerTax) {
      billToFields.push({ label: t('Tax Number:', 'الرقم الضريبي:'), value: customerTax });
    }

    // Company info fields
    const companyFields: { label: string; value: string }[] = [
      { label: t('Company:', 'الشركة:'), value: companyName },
    ];
    if (companyAddress) companyFields.push({ label: t('Address:', 'العنوان:'), value: companyAddress });
    if (companyTrn) companyFields.push({ label: t('TRN / VAT:', 'الرقم الضريبي / ضريبة القيمة المضافة:'), value: companyTrn });

    const maxRows = Math.max(billToFields.length, companyFields.length);
    for (let i = 0; i < maxRows; i++) {
      if (i < billToFields.length) {
        drawField(billToFields[i].label, billToFields[i].value, labelX, valueX, y);
      }
      if (i < companyFields.length) {
        drawField(companyFields[i].label, companyFields[i].value, companyLabelX, companyValueX, y);
      }
      y += 22;
    }

    // --- Section divider ---
    y += 10;
    drawSectionDivider(y);
    y += 20;

    // === SUBSCRIPTION / PLAN DETAILS ===
    ctx.font = `bold 15px ${fontFamily}`;
    ctx.fillStyle = '#2C3E50';
    ctx.textAlign = 'left';
    ctx.fillText(t('Subscription Details:', 'تفاصيل الاشتراك:'), labelX, y);
    y += 24;

    const currency = subPlan.currency || details.currency || mergedData.currency || 'USD';
    const currencySymbol = currency === 'AED' ? 'AED ' : '$';
    const billingMode = purchasedPlan.mode || plan.mode || mergedData.mode || 'N/A';
    const isYearly = billingMode.toLowerCase().includes('year') || billingMode.toLowerCase().includes('annual');

    const subscriptionFields: { label: string; value: string }[] = [
      { label: t('Plan Name:', 'اسم الخطة:'), value: subPlan.plan_name || purchasedPlan.type || plan.type || mergedData.plan_type || 'N/A' },
      { label: t('Billing Mode:', 'وضع الفوترة:'), value: billingMode },
      { label: t('Subscription Period:', 'فترة الاشتراك:'), value: `${purchasedPlan.start_date || mergedData.start_date || 'N/A'} - ${purchasedPlan.end_date || mergedData.end_date || 'N/A'}` },
      { label: t('Plan Status:', 'حالة الخطة:'), value: subPlan.status || 'N/A' },
      { label: t('Currency:', 'العملة:'), value: currency },
    ];

    // Pricing
    const monthlyPrice = subPlan.monthly_price;
    const yearlyPrice = subPlan.yearly_price;
    if (monthlyPrice != null) {
      subscriptionFields.push({ label: t('Monthly Price:', 'السعر الشهري:'), value: `${currencySymbol}${monthlyPrice}` });
    }
    if (yearlyPrice != null) {
      subscriptionFields.push({ label: t('Yearly Price:', 'السعر السنوي:'), value: `${currencySymbol}${yearlyPrice}` });
    }
    const unitPrice = isYearly ? (yearlyPrice ?? subPlan.price) : (monthlyPrice ?? subPlan.price);
    if (unitPrice != null) {
      subscriptionFields.push({ label: t('Unit Price:', 'سعر الوحدة:'), value: `${currencySymbol}${unitPrice}` });
    }

    // Contest & Participants
    const contestAllowed = subPlan.Contest_allowed ?? purchasedPlan.contest_allowed ?? plan.contest_allowed;
    if (contestAllowed != null) {
      subscriptionFields.push({ label: t('Contests Allowed:', 'المسابقات المسموحة:'), value: String(contestAllowed) });
    }
    if (purchasedPlan.contest_created != null) {
      subscriptionFields.push({ label: t('Contests Created:', 'المسابقات المنشأة:'), value: String(purchasedPlan.contest_created) });
    }
    if (subPlan.Participants != null) {
      subscriptionFields.push({ label: t('Participants:', 'المشاركون:'), value: String(subPlan.Participants) });
    }
    if (subPlan.Integrated_Contests != null) {
      subscriptionFields.push({ label: t('Integrated Contests:', 'المسابقات المدمجة:'), value: String(subPlan.Integrated_Contests) });
    }

    // Features
    if (subPlan.report_view || purchasedPlan.report_view) {
      const reportView = subPlan.report_view || purchasedPlan.report_view;
      subscriptionFields.push({ label: t('Report View:', 'عرض التقارير:'), value: reportView.charAt(0).toUpperCase() + reportView.slice(1) });
    }
    if (subPlan.Ads_View_Access != null) {
      subscriptionFields.push({ label: t('Ads View Access:', 'الوصول لعرض الإعلانات:'), value: subPlan.Ads_View_Access ? t('Yes', 'نعم') : t('No', 'لا') });
    }
    if (subPlan.Support_Access != null) {
      subscriptionFields.push({ label: t('Support Access:', 'الوصول للدعم:'), value: subPlan.Support_Access ? t('Yes', 'نعم') : t('No', 'لا') });
    }
    if (subPlan.VIP_View != null) {
      subscriptionFields.push({ label: t('VIP View:', 'عرض VIP:'), value: subPlan.VIP_View === '1' || subPlan.VIP_View === true ? t('Yes', 'نعم') : t('No', 'لا') });
    }
    if (subPlan.ai_attributes != null || purchasedPlan.ai_attributes != null) {
      subscriptionFields.push({ label: t('AI Attributes:', 'خصائص الذكاء الاصطناعي:'), value: (subPlan.ai_attributes || purchasedPlan.ai_attributes) ? t('Yes', 'نعم') : t('No', 'لا') });
    }
    if (subPlan.isEnterprise != null) {
      subscriptionFields.push({ label: t('Enterprise:', 'المؤسسات:'), value: subPlan.isEnterprise ? t('Yes', 'نعم') : t('No', 'لا') });
    }

    // SDK info
    const hasSdk = purchasedPlan.sdk_features || subPlan.sdk_features;
    subscriptionFields.push({ label: t('SDK Features:', 'ميزات SDK:'), value: hasSdk ? t('Yes', 'نعم') : t('No', 'لا') });
    if (hasSdk) {
      const sdkPrice = purchasedPlan.sdk_price ?? subPlan.sdk_price;
      if (sdkPrice != null) {
        subscriptionFields.push({ label: t('SDK Price:', 'سعر SDK:'), value: sdkPrice > 0 ? `${currencySymbol}${sdkPrice}` : t('Included', 'مشمول') });
      }
      const sdkBilling = purchasedPlan.sdk_billing_cycle || subPlan.sdk_billing_cycle;
      if (sdkBilling) {
        subscriptionFields.push({ label: t('SDK Billing:', 'فوترة SDK:'), value: sdkBilling.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) });
      }
    }

    // Discount on plan
    if (subPlan.discount_type && subPlan.discount_type !== '') {
      subscriptionFields.push({ label: t('Discount Type:', 'نوع الخصم:'), value: subPlan.discount_type.charAt(0).toUpperCase() + subPlan.discount_type.slice(1) });
      if (subPlan.monthly_discount) {
        subscriptionFields.push({ label: t('Monthly Discount:', 'الخصم الشهري:'), value: `${currencySymbol}${subPlan.monthly_discount}` });
      }
      if (subPlan.yearly_discount) {
        subscriptionFields.push({ label: t('Yearly Discount:', 'الخصم السنوي:'), value: `${currencySymbol}${subPlan.yearly_discount}` });
      }
    }

    // Render subscription fields in two columns
    const midX = 400;
    const rightLabelX = midX + 10;
    const rightValueX = midX + 150;
    const halfLen = Math.ceil(subscriptionFields.length / 2);

    for (let i = 0; i < halfLen; i++) {
      // Left column
      drawField(subscriptionFields[i].label, subscriptionFields[i].value, labelX, valueX, y);
      // Right column
      const rightIdx = i + halfLen;
      if (rightIdx < subscriptionFields.length) {
        drawField(subscriptionFields[rightIdx].label, subscriptionFields[rightIdx].value, rightLabelX, rightValueX, y);
      }
      y += 22;
    }

    // Description - rendered full width with word wrapping
    if (subPlan.description) {
      y += 6;
      ctx.font = `bold 12px ${fontFamily}`;
      ctx.fillStyle = '#2C3E50';
      ctx.textAlign = 'left';
      ctx.fillText(t('Description:', 'الوصف:'), labelX, y);
      y += 18;
      ctx.font = `12px ${fontFamily}`;
      ctx.fillStyle = '#3C3C3C';
      const maxWidth = rightEdge - labelX;
      const words = subPlan.description.split(' ');
      let line = '';
      for (const word of words) {
        const testLine = line ? line + ' ' + word : word;
        if (ctx.measureText(testLine).width > maxWidth && line) {
          ctx.fillText(line, labelX, y);
          line = word;
          y += 16;
        } else {
          line = testLine;
        }
      }
      if (line) {
        ctx.fillText(line, labelX, y);
        y += 16;
      }
    }

    // --- Section divider ---
    y += 10;
    drawSectionDivider(y);
    y += 20;

    // === PAYMENT DETAILS TABLE ===
    ctx.font = `bold 15px ${fontFamily}`;
    ctx.fillStyle = '#2C3E50';
    ctx.textAlign = 'left';
    ctx.fillText(t('Invoice Details:', 'تفاصيل الفاتورة:'), leftMargin, y);
    y += 22;

    const detColX = [leftMargin, leftMargin + 90, leftMargin + 180, leftMargin + 280, leftMargin + 380, leftMargin + 480, leftMargin + 580];
    const detHeaders = [
      t('Invoice ID', 'رقم الفاتورة'),
      t('Start Date', 'تاريخ البدء'),
      t('End Date', 'تاريخ الانتهاء'),
      t('Plan Type', 'نوع الخطة'),
      t('Mode', 'الوضع'),
      t('Amount', 'المبلغ'),
      t('Payment', 'الدفع'),
    ];

    // Header background
    ctx.fillStyle = '#2C3E50';
    ctx.fillRect(leftMargin, y - 2, rightEdge - leftMargin, 28);

    ctx.font = `bold 10px ${fontFamily}`;
    ctx.fillStyle = '#FFFFFF';
    detHeaders.forEach((header, i) => {
      ctx.textAlign = 'left';
      ctx.fillText(header, detColX[i] + 6, y + 17);
    });

    // Detail row
    y += 28;
    ctx.fillStyle = '#F9F9F9';
    ctx.fillRect(leftMargin, y - 2, rightEdge - leftMargin, 26);

    const detValues = [
      String(mergedData.id || 'N/A'),
      mergedData.start_date || 'N/A',
      mergedData.end_date || 'N/A',
      mergedData.plan_type || 'N/A',
      mergedData.mode || 'N/A',
      mergedData.paid_amount || `${currencySymbol}0`,
      (mergedData.payment_method || 'N/A')
    ];

    ctx.font = `11px ${fontFamily}`;
    ctx.fillStyle = '#323232';
    detValues.forEach((val, i) => {
      ctx.textAlign = 'left';
      ctx.fillText(val, detColX[i] + 6, y + 16);
    });

    // --- Section divider ---
    y += 30;
    drawSectionDivider(y);
    y += 25;

    // === PAYMENT SUMMARY ===
    ctx.font = `bold 15px ${fontFamily}`;
    ctx.fillStyle = '#2C3E50';
    ctx.textAlign = 'left';
    ctx.fillText(t('Payment Summary:', 'ملخص الدفع:'), leftMargin, y);
    y += 24;

    const summaryLabelX = leftMargin;
    const summaryValueX = leftMargin + 180;

    const discountType = mergedData._discount_type || '';
    const rawOriginal = mergedData._raw_original_amount;
    const rawDiscounted = mergedData._raw_discounted_amount;
    const rawSaved = String(mergedData._raw_discount_saved ?? '');
    const hasDiscountData = rawSaved !== '' && rawSaved !== 'N/A';

    // Subtotal
    const subtotal = rawOriginal ?? details.subtotal;
    if (typeof subtotal === 'number') {
      drawField(t('Subtotal:', 'المجموع الفرعي:'), `${currencySymbol}${subtotal % 1 === 0 ? subtotal : subtotal.toFixed(2)}`, summaryLabelX, summaryValueX, y);
      y += 22;
    }

    // Discount / Price Adjustment
    if (discountType) {
      const displayType = discountType === 'percentage' ? t('Percentage', 'نسبة مئوية') : discountType === 'fixed' ? t('Fixed', 'ثابت') : discountType.charAt(0).toUpperCase() + discountType.slice(1);
      drawField(t('Discount Type:', 'نوع الخصم:'), displayType, summaryLabelX, summaryValueX, y);
      y += 22;
    }
    if (hasDiscountData) {
      ctx.font = `bold 12px ${fontFamily}`;
      ctx.fillStyle = '#2C3E50';
      ctx.textAlign = 'left';
      ctx.fillText(t('Price Adjustment:', 'تعديل السعر:'), summaryLabelX, y);
      ctx.font = `bold 12px ${fontFamily}`;
      const isNegativeSaved = rawSaved.startsWith('-');
      const absValue = rawSaved.replace('+', '').replace('-', '');
      ctx.fillStyle = isNegativeSaved ? '#27AE60' : '#E74C3C';
      ctx.fillText(`${isNegativeSaved ? '-' : '+'}${currencySymbol}${absValue}`, summaryValueX, y);
      y += 22;
    }

    // VAT line
    const vatRate = details.vat_rate ?? details.tax_rate;
    const vatAmount = details.vat_amount ?? details.tax_amount;
    if (vatRate != null || vatAmount != null) {
      const vatLabel = vatRate != null ? `${t('VAT', 'ضريبة القيمة المضافة')} (${vatRate}%):` : t('VAT:', 'ضريبة القيمة المضافة:');
      const vatValue = vatAmount != null ? `${currencySymbol}${vatAmount}` : t('Not Applicable', 'غير منطبق');
      drawField(vatLabel, vatValue, summaryLabelX, summaryValueX, y);
    } else {
      drawField(t('VAT:', 'ضريبة القيمة المضافة:'), t('Not Applicable', 'غير منطبق'), summaryLabelX, summaryValueX, y);
    }
    y += 22;

    // Separator line
    y += 4;
    ctx.strokeStyle = '#C8C8C8';
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(summaryLabelX, y);
    ctx.lineTo(summaryLabelX + 280, y);
    ctx.stroke();
    y += 16;

    // Total Amount
    const totalAmount = rawDiscounted ?? details.total_amount ?? mergedData.paid_amount ?? `${currencySymbol}0`;
    const totalDisplay = typeof totalAmount === 'number' ? `${currencySymbol}${totalAmount}` : String(totalAmount);
    ctx.font = `bold 14px ${fontFamily}`;
    ctx.fillStyle = '#2C3E50';
    ctx.textAlign = 'left';
    ctx.fillText(t('Total Amount:', 'المبلغ الإجمالي:'), summaryLabelX, y);
    ctx.fillText(totalDisplay, summaryValueX, y);
    y += 22;

    // Currency
    drawField(t('Currency:', 'العملة:'), `${currency}`, summaryLabelX, summaryValueX, y);
    y += 22;

    // Payment Date
    const paymentDate = details.payment_date || details.paid_at || mergedData.created_date || mergedData.start_date || '';
    drawField(t('Payment Date:', 'تاريخ الدفع:'), paymentDate || 'N/A', summaryLabelX, summaryValueX, y);
    y += 22;

    // Payment Method
    const paymentMethod = details.payment_method_detail || details.payment_method || mergedData.payment_method || 'N/A';
    drawField(t('Payment Method:', 'طريقة الدفع:'), paymentMethod.toUpperCase(), summaryLabelX, summaryValueX, y);
    y += 22;

    // Payment Reference (optional)
    const paymentRef = details.transaction_id || details.authorization_id || details.payment_reference || details.last_4_digits || '';
    if (paymentRef) {
      drawField(t('Payment Ref:', 'مرجع الدفع:'), String(paymentRef), summaryLabelX, summaryValueX, y);
      y += 22;
    }

    // Payment Status
    ctx.font = `bold 12px ${fontFamily}`;
    ctx.fillStyle = '#2C3E50';
    ctx.textAlign = 'left';
    ctx.fillText(t('Payment Status:', 'حالة الدفع:'), summaryLabelX, y);
    const status = (mergedData.payment_status || purchasedPlan.paymentstatus || 'N/A').toUpperCase();
    ctx.fillStyle = status === 'PAID' ? '#27AE60' : '#E74C3C';
    ctx.font = `bold 12px ${fontFamily}`;
    ctx.fillText(status, summaryValueX, y);

    // === FOOTER ===
    const footerY = Math.max(y + 50, pageHeight - 50);
    ctx.strokeStyle = '#C8C8C8';
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(leftMargin, footerY);
    ctx.lineTo(rightEdge, footerY);
    ctx.stroke();

    ctx.textAlign = 'center';
    ctx.font = `12px ${fontFamily}`;
    ctx.fillStyle = '#787878';
    ctx.fillText(t('Thank you for your business!', 'شكرًا لتعاملكم معنا!'), 400, footerY + 25);
    ctx.fillText(`${t('Generated on', 'تم الإنشاء في')} ${today} | LUQTA`, 400, footerY + 42);

    // === CONVERT TO PDF ===
    const imgData = canvas.toDataURL('image/jpeg', 0.98);
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`Invoice_${mergedData.id || 'download'}.pdf`);
    this.sharedService.hideLoader();
    this.sharedService.createNotification('success', 'Invoice downloaded successfully');
  }

  private async enrichInvoiceWithDiscount(data: any): Promise<any> {
    try {
      // Fetch all discount records
      const subscriptionId = data?.billing_payment_id ?? data?.invoice_id ?? data?.id ?? 0;
      const response: any = await firstValueFrom(this.pageService.getAll('BILLING_DISCOUNT', { subscription_id: subscriptionId }));
      const history = Array.isArray(response?.history) ? response.history : [];
      const user = response?.user || {};

      const enriched: any = {
        ...data,
        _user: { ...data._user, ...user },
      };

      // API returns filtered history for this subscription, use first match
      const match = history.length > 0 ? history[0] : null;

      if (match) {
        const isYearly = String(match.billing_cycle).toLowerCase().includes('annual') || String(match.billing_cycle).toLowerCase().includes('year');
        const originalAmount = isYearly ? match.original_yearly_price : match.original_monthly_price;
        const discountValue = isYearly ? match.yearly_discount_value : match.monthly_discount_value;
        enriched.original_amount = originalAmount != null ? this.formatCurrency(originalAmount) : data.original_amount;
        enriched.discounted_amount = match.discounted_amount != null ? this.formatCurrency(match.discounted_amount) : data.discounted_amount;
        enriched._raw_original_amount = originalAmount;
        enriched._raw_discounted_amount = match.discounted_amount;
        enriched._raw_discount_saved = match.discount_saved;
        enriched._discount_type = match.discount_type || '';
        enriched._discount_value = discountValue;
        enriched.discount_saved = match.discount_saved != null ? match.discount_saved : 'N/A';
        enriched.paid_amount = data.paid_amount;
      }

      return enriched;
    } catch (error) {
      console.error('Failed to fetch discount for invoice:', error);
      return data;
    }
  }

  private async loadCairoFont(): Promise<void> {
    if (document.querySelector('link[href*="Cairo"]')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Cairo:wght@400;700&display=swap';
    document.head.appendChild(link);
    // Wait a bit for font to start loading
    await new Promise(resolve => setTimeout(resolve, 300));
  }

  private loadImageForCanvas(src: string): Promise<HTMLImageElement | null> {
    return new Promise(resolve => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = src;
    });
  }

  private async getLuqtaLogoDataUrl(): Promise<string | null> {
    if (this.luqtaLogoDataUrl) {
      return this.luqtaLogoDataUrl;
    }

    const logoPaths = [this.luqtaLogoPrimaryPath, this.luqtaLogoFallbackPath];

    for (const path of logoPaths) {
      try {
        const response = await fetch(path);
        if (!response.ok) {
          continue;
        }

        const blob = await response.blob();
        const dataUrl = await this.readBlobAsDataUrl(blob);

        if (dataUrl) {
          this.luqtaLogoDataUrl = dataUrl;
          return dataUrl;
        }
      } catch {
        // Try next logo path.
      }
    }

    return null;
  }

  private readBlobAsDataUrl(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Failed to read logo file'));
      reader.readAsDataURL(blob);
    });
  }

}

